/* eslint-disable @typescript-eslint/no-explicit-any -- Firestore/Supabase interop requires dynamic typing for refs and sanitization */
/**
 * Sanitize an object for Firestore by removing/converting non-serializable values.
 * Handles mock timestamps (objects with toDate function) and mock refs from Supabase data.
 *
 * Extracted from sales.ts into its own module so it can be unit-tested without pulling in
 * the heavy sales.ts dependency graph (Sentry/react-native/jotai). Mirrors the standalone
 * `productSanitize.ts`. Behaviour is intentionally identical to the previous inline version,
 * except undefined values are now dropped (QARASAY-KASSA-2AC).
 *
 * Uses 'any' type because this function processes deeply nested objects with varying shapes
 * from both Firestore (with Timestamps, DocumentReferences) and Supabase (with mock objects).
 * The recursive nature and duck-typing checks (e.g., checking for toDate method) require
 * dynamic typing that cannot be expressed with strict TypeScript interfaces.
 *
 * @param obj - Object to sanitize (can be primitives, arrays, or complex nested objects)
 * @returns Sanitized object safe for Firestore, with same structure but serializable values
 */
export const sanitizeForFirestore = (obj: any, seen: WeakSet<object> = new WeakSet()): any => {
  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj
  if (obj instanceof Date) return obj

  // Check if it's a mock timestamp (has toDate function but isn't a real Firestore Timestamp)
  if (typeof obj.toDate === 'function') {
    // Check if it's a real Firestore Timestamp (has _seconds property or is from firebase)
    if (obj._seconds !== undefined || obj.seconds !== undefined) {
      // It's likely a real or mock timestamp - convert to Date
      try {
        return obj.toDate()
      } catch {
        // Silent catch intentional: toDate() may fail on malformed timestamp data
        // during sanitization - returning null is the safe fallback for Firestore
        return null
      }
    }
    // It's a mock timestamp from Supabase transformer - convert to Date
    try {
      return obj.toDate()
    } catch {
      // Silent catch intentional: toDate() may fail on malformed timestamp data
      // during sanitization - returning null is the safe fallback for Firestore
      return null
    }
  }

  // Check if it's a mock ref (has path property and update/delete/get methods)
  if (obj.path && typeof obj.update === 'function' && typeof obj.delete === 'function') {
    // Don't include mock refs in the saved data
    return null
  }

  // Cycle guard (DAG-safe: add on enter, delete on exit so shared/diamond refs are kept but a
  // true back-edge is dropped). Cart items can carry a live cyclic Firestore ref under non-`ref`
  // keys; an unguarded recurse would blow the stack (RangeError, ATL-554 class). Mirrors
  // productSanitize.ts. A surviving cycle is anomalous, so make the drop observable.
  if (seen.has(obj as object)) {
    console.warn('[salesSanitize] dropped cyclic value during sanitization')
    return null
  }
  seen.add(obj as object)

  let result: any
  if (Array.isArray(obj)) {
    result = obj.map(item => sanitizeForFirestore(item, seen))
  } else {
    // Recursively sanitize object properties
    const sanitized: any = {}
    for (const [key, value] of Object.entries(obj)) {
      // Skip ref fields entirely - they shouldn't be saved to Firestore
      if (key === 'ref' || key === 'cartItemRef') continue

      const sanitizedValue = sanitizeForFirestore(value, seen)
      // Drop undefined entirely — Firestore's batch.set rejects undefined field values
      // (e.g. deliveryIncludeInReceipt on stores whose saleSettings predate the toggle, QARASAY-KASSA-2AC).
      // Only include non-null values (or explicitly set null for some fields).
      if (sanitizedValue === undefined) continue
      if (sanitizedValue !== null || key === 'clientId' || key === 'clientPhone') {
        sanitized[key] = sanitizedValue
      }
    }
    result = sanitized
  }

  // Allow the same object to reappear on a sibling branch (DAG, not a cycle).
  seen.delete(obj as object)
  return result
}

/**
 * Heavy product-catalog fields that bloat a cart line. During dual-write the frontend passes
 * whole product objects as cart items (Principle #6 pass-through), which can push a sale's
 * request body past the backend body-parser limit → PayloadTooLargeError (ATL-608 /
 * QARASAY-KASSA-29Y, a >5MB PATCH /sales/:id). These fields are `@Allow()`-ed by the sale DTO
 * but explicitly NOT stored by the backend ("Mutable product state … is NOT stored",
 * create-sale.dto.ts), so dropping them from the Supabase payload is loss-free. The Firestore
 * write keeps the full shape. NB: `salePrices` and `ingredients` ARE consumed by the backend
 * (price normalization / composite stock) and are deliberately kept.
 */
const HEAVY_PRODUCT_CART_FIELDS = [
  'priceTypes',
  'priceTypeScopes',
  'storeStock',
  'activeInStore',
  'barcodes',
  'productTags',
  'variants',
  'units',
  'productImages',
] as const

export const stripHeavyProductCartFields = (item: any): any => {
  if (!item || typeof item !== 'object') return item
  const out: any = { ...item }
  for (const f of HEAVY_PRODUCT_CART_FIELDS) delete out[f]
  return out
}
